``;
import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  VStack,
  Select as ChakraSelect,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';

import countryCodes, { CountryProperty } from 'country-codes-list';

import {
  useCreateTeamMemberMutation,
  useGetAllRolesQuery,
  useGetTeamMemberByIdQuery,
  useUpdateTeamMemberMutation,
} from '../../../services';

import { TeamMemberForm } from '../models';
import { CenteredSpinner } from '../components';
import { getApiErrorMessage, getBodyFailureMessage } from '../utils';
import { BasicInfo, Status } from '../../../models';

const options = countryCodes.all().map(country => ({
  value: country.countryCallingCode,
}));

export const MemberInfo: FC = () => {
  const [basicInfo, setBasicInfo] = useState<BasicInfo>();
  const [teamMemberStatus, setTeamMemberStatus] = useState<Status>();

  const navigate = useNavigate();

  const params = useParams();
  const { companyId, memberId } = params;
  const status = params.status || 'all';

  const { data: teamMember, isLoading } = useGetTeamMemberByIdQuery(
    Number(memberId),
    {
      skip: !memberId,
    }
  );

  const { data: allRoles } = useGetAllRolesQuery(Number(companyId));

  const [createTeamMember] = useCreateTeamMemberMutation();
  const [updateTeamMember] = useUpdateTeamMemberMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<TeamMemberForm>();

  const toast = useToast();

  const navigateToPreviousPage = () =>
    navigate(`/companies/${status}/${companyId}/team`);

  async function onSubmit(data: TeamMemberForm) {
    const isUpdate = Boolean(teamMember?.data);
    try {
      const result = isUpdate
        ? await updateTeamMember({
            ...data,
            id: Number(memberId),
            companyBasicInfo: basicInfo,
            countryCode: data.countryCode.value,
            status: teamMemberStatus,
          }).unwrap()
        : await createTeamMember({
            ...data,
            companyBasicInfo: { id: Number(companyId) },
            role: { id: data.role },
            countryCode: data.countryCode.value,
          }).unwrap();

      // core-api reports business failures INSIDE a 200 body, so `.unwrap()`
      // resolves on them: classify the envelope (errorCode, canonical) before
      // claiming the member was saved and before promising an invitation email
      // that was never sent. Body failures now reach the same error toast that
      // only transport failures used to reach.
      const failure = getBodyFailureMessage(result);
      if (failure) {
        toast({
          title: 'Could not save team member',
          description: failure,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: isUpdate ? 'Team member updated' : 'Team member added',
        description: isUpdate
          ? undefined
          : 'An invitation email has been sent so they can set a password and sign in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      toast({
        title: 'Could not save team member',
        description: getApiErrorMessage(error),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  useEffect(() => {
    if (teamMember?.data) {
      const {
        companyBasicInfo,
        countryCode,
        email,
        firstName,
        lastName,
        phoneNumber,
        status,
      } = teamMember.data;

      reset({
        countryCode: { value: countryCode },
        email,
        firstName,
        lastName,
        phoneNumber,
      });

      setBasicInfo(companyBasicInfo);
      setTeamMemberStatus(status);
    }
  }, [teamMember?.data, isLoading, reset]);

  if (isLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          Contact Info
        </Heading>
      </HStack>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              First Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.firstName} flexBasis="50%">
            <Input
              placeholder="First Name"
              {...register('firstName', {
                required: 'First name is required',
              })}
            />
            <FormErrorMessage>
              {errors.firstName && errors.firstName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Last Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.lastName} flexBasis="50%">
            <Input
              placeholder="Last Name"
              {...register('lastName', {
                required: 'Last name is required',
              })}
            />
            <FormErrorMessage>
              {errors.lastName && errors.lastName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Phone Number
            </Box>
          </VStack>
          <FormControl
            isInvalid={!!errors.phoneNumber || !!errors.countryCode}
            flexBasis="50%"
          >
            <HStack>
              <Box flexBasis="40%">
                <Controller
                  control={control}
                  name="countryCode"
                  rules={{
                    required: 'Country code is required',
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={options}
                      getOptionLabel={({ value }) => value}
                      formatOptionLabel={({ value }) => {
                        const country = countryCodes.findOne(
                          'countryCallingCode' as CountryProperty,
                          value
                        );

                        return (
                          <div className="flex items-center">
                            <span
                              className={`fi fi-${country.countryCode.toLowerCase()} fis`}
                            ></span>
                            <span
                              style={{
                                marginLeft: 5,
                                width: '80px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {value}
                            </span>
                          </div>
                        );
                      }}
                    />
                  )}
                />
              </Box>

              <Input
                placeholder="Phone number"
                {...register('phoneNumber', {
                  required: 'Phone number is required',
                })}
              />
            </HStack>
            <FormErrorMessage>
              {(errors.phoneNumber && errors.phoneNumber.message) ||
                (errors.countryCode && errors.countryCode.message)}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Email
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.email} flexBasis="50%">
            <Input
              placeholder="Email"
              {...register('email', {
                required: 'Email is required',
              })}
            />
            <FormErrorMessage>
              {errors.email && errors.email.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Role
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.role} flexBasis="50%">
            <ChakraSelect
              {...register('role', {
                required: 'Role is required',
              })}
            >
              {allRoles?.data.content.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </ChakraSelect>
            <FormErrorMessage>
              {errors.role && errors.role.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            {memberId ? 'Save' : 'Add'}
          </Button>
        </HStack>
      </form>
    </Box>
  );
};
